import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OffersBlock from "@/components/seo/OffersBlock";
import SeoSiloLinks from "@/components/seo/SeoSiloLinks";
import PopularPagesBlock from "@/components/seo/PopularPagesBlock";
import RecentPagesBlock from "@/components/seo/RecentPagesBlock";
import EditorialEnrichment from "@/components/seo/EditorialEnrichment";
import Breadcrumbs from "@/components/Breadcrumbs";
import LastUpdated from "@/components/LastUpdated";
import { Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { Film, Smartphone, Zap, Shield, Clock, Star, ArrowRight, ChevronRight, Clapperboard, Globe, Timer, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TestRequestFormWithCountries from "@/components/TestRequestFormWithCountries";
import FloatingTestCTA from "@/components/FloatingTestCTA";
import { useSeoPageView } from "@/hooks/useSeoPageView";
import { buildArticleSchema, organizationSchema } from "@/lib/seo-schemas";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";




// ── Ligne de fiche technique ─────────────────────────────────────────────────
const InfoRow = ({
  icon: Icon, label, value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) => (
  <tr className="border-b border-border/40 last:border-0">
    <th className="text-left px-4 py-3 bg-card/50 font-medium w-5/12">
      <div className="flex items-center gap-2 text-muted-foreground text-sm">
        <Icon className="w-3.5 h-3.5 shrink-0" />
        {label}
      </div>
    </th>
    <td className="px-4 py-3 text-foreground text-sm">{value}</td>
  </tr>
);

// ── Composant principal ──────────────────────────────────────────────────────
const MoviePage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: movie, isLoading } = useQuery({
    queryKey: ["movie", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("movies")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  // 3 films liés pour "Voir aussi"
  const { data: relatedMovies } = useQuery({
    queryKey: ["movies-related", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("movies")
        .select("slug, title, genres, release_date, vote_average, poster_url")
        .eq("published", true)
        .neq("slug", slug!)
        .order("created_at", { ascending: false })
        .limit(3);
      return data || [];
    },
    enabled: !!slug,
  });

  // ✅ Hook avant tout return conditionnel
  useSeoPageView("movie", slug, movie?.title);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!movie) return <NotFound />;

  // ── Extraction des données ──
  const isReleased = movie.release_date ? new Date(movie.release_date) <= new Date() : false;
  const overview = (movie as any).overview as string | null;
  const genres = (movie as any).genres as string | null;
  const voteAverage = (movie as any).vote_average as number | null;
  const backdropUrl = (movie as any).backdrop_url as string | null;
  const runtime = (movie as any).runtime as number | null;
  const tagline = (movie as any).tagline as string | null;
  const originalTitle = (movie as any).original_title as string | null;
  const originalLanguage = (movie as any).original_language as string | null;
  const director = (movie as any).director as string | null;
  const castList = (movie as any).cast_list as string | null;

  const runtimeLabel = runtime
    ? `${Math.floor(runtime / 60)}h${String(runtime % 60).padStart(2, "0")}`
    : null;

  const releaseDateFormatted = movie.release_date
    ? new Date(movie.release_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const releaseYear = movie.release_date ? new Date(movie.release_date).getFullYear() : null;

  const synopsisSnippet = overview ? overview.substring(0, 160) + "…" : "";

  const title = isReleased
    ? `${movie.title}${releaseYear ? ` (${releaseYear})` : ""} Streaming VF HD | RoiDuStreaming`
    : `${movie.title} - Date de Sortie & Infos Streaming | RoiDuStreaming`;

  const description = overview
    ? `${movie.title} en streaming VF HD et 4K. ${synopsisSnippet} Disponible sur RoiDuStreaming.`
    : isReleased
      ? `Regardez ${movie.title} en streaming VF HD et 4K. ${genres ? `Film ${genres.toLowerCase()}.` : ""} +100 000 films sur RoiDuStreaming.`
      : `${movie.title} arrive bientôt ! Préparez-vous à le regarder en streaming VF HD sur RoiDuStreaming.`;

  const canonicalUrl = `https://roidustreaming.fr/films/${movie.slug}`;
  const imageAlt = `${movie.title} streaming VF HD`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: movie.title,
    ...(originalTitle && originalTitle !== movie.title && { alternateName: originalTitle }),
    description: overview || description,
    datePublished: movie.release_date,
    image: movie.poster_url,
    url: canonicalUrl,
    ...(genres && { genre: genres.split(", ") }),
    ...(director && { director: { "@type": "Person", name: director } }),
    ...(runtime && { duration: `PT${runtime}M` }),
    ...(voteAverage && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: voteAverage,
        bestRating: 10,
        ratingCount: 500,
      },
    }),
    provider: {
      "@type": "Organization",
      name: "RoiDuStreaming",
      url: "https://roidustreaming.fr",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Où regarder ${movie.title} en streaming VF ?`,
        acceptedAnswer: { "@type": "Answer", text: `Regardez ${movie.title} en streaming VF HD et 4K sur RoiDuStreaming. Abonnez-vous à partir de 5€/mois ou testez gratuitement pendant 24h.` },
      },
      {
        "@type": "Question",
        name: `${movie.title} est-il disponible en version française (VF) ?`,
        acceptedAnswer: { "@type": "Answer", text: `Oui, ${movie.title} est disponible en VF (version française) et VOSTFR (sous-titrée) en qualité Full HD et 4K sur RoiDuStreaming.` },
      },
      {
        "@type": "Question",
        name: `${movie.title} est-il en 4K ?`,
        acceptedAnswer: { "@type": "Answer", text: `Oui, ${movie.title} est disponible en Full HD et 4K sur RoiDuStreaming, sans publicité ni coupure.` },
      },
      {
        "@type": "Question",
        name: `Sur quels appareils puis-je regarder ${movie.title} ?`,
        acceptedAnswer: { "@type": "Answer", text: `${movie.title} est accessible sur Smart TV (Samsung, LG, Sony), Android, iPhone/iPad, PC Windows, Mac et Amazon Fire Stick.` },
      },
      ...(runtimeLabel
        ? [{
            "@type": "Question",
            name: `Quelle est la durée de ${movie.title} ?`,
            acceptedAnswer: { "@type": "Answer", text: `${movie.title} dure ${runtimeLabel} (${runtime} minutes).` },
          }]
        : []),
    ],
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Films", href: "/films" },
    { label: movie.title, href: `/films/${movie.slug}` },
  ];

  const advantages = [
    { icon: Film, title: "Full HD & 4K", desc: "Qualité cinéma sur tous vos écrans" },
    { icon: Zap, title: "Streaming fluide", desc: "Zéro buffering, zéro lag" },
    { icon: Smartphone, title: "Multi-appareils", desc: "Smart TV, mobile, tablette, PC" },
    { icon: Shield, title: "Accès sécurisé", desc: "Paiement Stripe 100% sécurisé" },
    { icon: Clock, title: "Nouveautés rapides", desc: "Films ajoutés dès leur sortie" },
    { icon: Star, title: "100 000+ films", desc: "Le plus grand catalogue VF/VOSTFR" },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://roidustreaming.fr" },
      { "@type": "ListItem", position: 2, name: "Films", item: "https://roidustreaming.fr/films" },
      { "@type": "ListItem", position: 3, name: movie.title, item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="video.movie" />
        <meta property="og:site_name" content="RoiDuStreaming" />
        <meta property="og:locale" content="fr_FR" />
        {(backdropUrl || movie.poster_url) && <meta property="og:image" content={backdropUrl || movie.poster_url!} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {movie.release_date && <meta property="article:published_time" content={`${movie.release_date}T00:00:00Z`} />}
        <meta property="article:section" content="Films" />
        {genres && genres.split(", ").map((g) => <meta key={g} property="article:tag" content={g} />)}
        {director && <meta property="article:tag" content={director} />}
        <link rel="preconnect" href="https://tpujpzojoqwytuhgbwtw.supabase.co" />
        <link rel="preconnect" href="https://image.tmdb.org" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(buildArticleSchema({
          headline: movie.title + (releaseYear ? ` (${releaseYear})` : "") + " Streaming VF HD",
          description,
          url: canonicalUrl,
          imageUrl: backdropUrl || movie.poster_url,
          datePublished: movie.created_at || movie.release_date || new Date().toISOString(),
          dateModified: movie.created_at || new Date().toISOString(),
          section: "Films",
        }))}</script>
      </Helmet>

      {/* ── NOSCRIPT FALLBACK : données critiques visibles sans JS ── */}
      <noscript>
        <div>
          <h1>{movie.title}{releaseYear ? ` (${releaseYear})` : ""} – Streaming VF HD</h1>
          {overview && <p>{overview.substring(0, 250)}</p>}
          {genres && <p>Genre : {genres}.</p>}
          {director && <p>Réalisé par {director}.</p>}
          {releaseDateFormatted && <p>Sortie le {releaseDateFormatted}.</p>}
          {runtimeLabel && <p>Durée : {runtimeLabel}.</p>}
          <p>Regardez {movie.title} en streaming VF et 4K sur RoiDuStreaming, à partir de 5€/mois.</p>
          <ul>
            <li><a href="https://roidustreaming.fr">Accueil</a></li>
            <li><a href="https://roidustreaming.fr/films">Films en streaming VF</a></li>
            <li><a href="https://roidustreaming.fr/nos-offres">Offres IPTV</a></li>
          </ul>
        </div>
      </noscript>

      <article className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs items={breadcrumbItems} />
        <div className="max-w-6xl mx-auto px-4 pt-3">
          <LastUpdated date={movie.created_at || new Date().toISOString()} />
        </div>

        {/* ── HERO ──────────────────────────────────────────────────────── */}
        <section className="relative min-h-[80vh] flex items-center px-4 overflow-hidden">
          {/* Backdrop flouté */}
          {(backdropUrl || movie.poster_url) && (
            <img
              src={backdropUrl || movie.poster_url!}
              alt={imageAlt}
              className="absolute inset-0 w-full h-full object-cover blur-sm scale-105 opacity-30"
              loading="eager"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/70" />

          <div className="relative z-10 max-w-6xl mx-auto w-full flex flex-col lg:flex-row items-start gap-8 lg:gap-12 py-16">

            {/* ── Colonne texte (gauche) ── */}
            <div className="flex-1 min-w-0 order-2 lg:order-1">

              {/* Badge dispo */}
              <span className={`inline-block mb-5 px-4 py-1.5 rounded-full text-sm font-bold ${isReleased ? "bg-secondary/20 text-secondary border border-secondary/30" : "bg-yellow-400/10 text-yellow-400 border border-yellow-400/30"}`}>
                {isReleased ? "✅ Disponible maintenant" : "⏳ Bientôt disponible"}
              </span>

              {/* Titre + année */}
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-2 text-foreground leading-tight">
                {movie.title}
                {releaseYear && <span className="text-muted-foreground font-normal ml-3 text-2xl md:text-3xl">({releaseYear})</span>}
              </h1>

              {/* Titre original */}
              {originalTitle && originalTitle !== movie.title && (
                <p className="text-muted-foreground text-sm mb-3 italic font-light">
                  Titre original : <span className="text-foreground/70">{originalTitle}</span>
                </p>
              )}

              {/* Tagline en italique */}
              {tagline && (
                <p className="text-primary/80 italic text-base md:text-xl mb-5 font-medium border-l-2 border-primary/40 pl-3">
                  "{tagline}"
                </p>
              )}

              {/* Meta-infos inline : date | durée | langue */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5 text-sm text-muted-foreground">
                {releaseDateFormatted && (
                  <span className="flex items-center gap-1.5">
                    <Film className="w-3.5 h-3.5 text-primary/60" />
                    {releaseDateFormatted}
                  </span>
                )}
                {runtimeLabel && (
                  <>
                    <span className="text-border">·</span>
                    <span className="flex items-center gap-1.5">
                      <Timer className="w-3.5 h-3.5 text-primary/60" />
                      {runtimeLabel}
                    </span>
                  </>
                )}
                {originalLanguage && (
                  <>
                    <span className="text-border">·</span>
                    <span className="flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-primary/60" />
                      {originalLanguage}
                    </span>
                  </>
                )}
              </div>

              {/* Genres */}
              {genres && (
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  {genres.split(", ").map((g) => (
                    <Badge key={g} variant="outline" className="border-primary/40 text-primary text-xs">{g}</Badge>
                  ))}
                </div>
              )}

              {/* ── BLOC IMDb DANS LE HERO ── */}
              {voteAverage && voteAverage > 0 && (
                <div className="mb-5 p-4 rounded-2xl bg-black/40 border border-yellow-400/30 backdrop-blur-sm w-fit max-w-xs">
                  {/* Badge + score */}
                  <div className="flex items-end gap-3 mb-2">
                    <span className="text-[11px] font-black tracking-[0.2em] text-black bg-yellow-400 px-2.5 py-1 rounded-md uppercase leading-none">IMDb</span>
                    <span className="text-5xl font-black text-yellow-400 leading-none tabular-nums">{voteAverage.toFixed(1)}</span>
                    <span className="text-muted-foreground text-base mb-1">/10</span>
                  </div>
                  {/* Étoiles */}
                  <div className="flex items-center gap-1 mb-2.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-5 h-5 ${s <= Math.round(voteAverage / 2) ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/25 fill-muted-foreground/10"}`}
                      />
                    ))}
                    <span className="ml-2 text-xs text-muted-foreground">Note des spectateurs</span>
                  </div>
                  {/* Barre de progression colorée */}
                  <div className="w-full bg-muted/30 rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${voteAverage >= 7 ? "bg-green-400" : voteAverage >= 5 ? "bg-orange-400" : "bg-red-500"}`}
                      style={{ width: `${(voteAverage / 10) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Réalisateur sous le hero */}
              {director && (
                <p className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
                  <Clapperboard className="w-4 h-4 shrink-0 text-primary/60" />
                  Réalisé par <span className="text-foreground font-semibold ml-1">{director}</span>
                </p>
              )}

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3">
                <TestRequestFormWithCountries
                  trigger={
                    <Button className="btn-primary text-primary-foreground font-bold py-5 sm:py-6 px-6 sm:px-8 text-sm sm:text-base rounded-full group w-full sm:w-auto">
                      🎬 Essayer gratuitement 24h
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  }
                />
                <Link to="/nos-offres" className="w-full sm:w-auto">
                  <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 font-bold py-5 sm:py-6 px-6 sm:px-8 text-sm sm:text-base rounded-full w-full sm:w-auto">
                    Voir les offres dès 5€/mois
                  </Button>
                </Link>
              </div>
            </div>

            {/* ── Colonne poster (droite) ── */}
            <div className="shrink-0 order-1 lg:order-2 w-full lg:w-auto flex justify-center lg:justify-end">
              {movie.poster_url && (
                <img
                  src={movie.poster_url}
                  alt={imageAlt}
                  title={`${movie.title} streaming VF`}
                  className="w-44 sm:w-56 lg:w-72 aspect-[2/3] object-cover rounded-2xl shadow-2xl ring-2 ring-yellow-400/20"
                  loading="eager"
                  width="288"
                  height="432"
                />
              )}
            </div>
          </div>
        </section>

        {/* ── Barre de confiance ─────────────────────────────────────────── */}
        <section className="border-y border-border/50 bg-card/50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <span>⚡ Installation en 5 min</span>
            <span>🛟 Support 7j/7</span>
            <span>📺 +15 000 chaînes TV</span>
            <span>🔒 Paiement sécurisé</span>
          </div>
        </section>

        {/* ── Synopsis ──────────────────────────────────────────────────── */}
        {overview && (
          <section className="py-12 px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-foreground">Synopsis de {movie.title}</h2>
              <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                {overview.length > 300
                  ? <>{overview.substring(0, 300)}<span className="hidden sm:inline">{overview.substring(300)}</span><span className="sm:hidden text-primary">… <span className="italic text-xs">suite ci-dessous</span></span></>
                  : overview
                }
              </p>
              {overview.length > 300 && (
                <p className="sm:hidden text-sm text-muted-foreground mt-3 leading-relaxed">
                  {overview.substring(300)}
                </p>
              )}
            </div>
          </section>
        )}

        {/* ── FICHE TECHNIQUE ──────────────────────────────────────────── */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Fiche technique – {movie.title}</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <tbody>
                  <InfoRow icon={Film} label="Titre" value={<span className="font-semibold">{movie.title}</span>} />
                  {originalTitle && originalTitle !== movie.title && (
                    <InfoRow icon={Film} label="Titre original" value={<span className="italic">{originalTitle}</span>} />
                  )}
                  {releaseDateFormatted && (
                    <InfoRow icon={Clock} label="Date de sortie" value={releaseDateFormatted} />
                  )}
                  {runtimeLabel && (
                    <InfoRow icon={Timer} label="Durée" value={<span>{runtimeLabel} <span className="text-muted-foreground text-xs">({runtime} min)</span></span>} />
                  )}
                  {genres && (
                    <InfoRow
                      icon={Film}
                      label="Genres"
                      value={
                        <div className="flex flex-wrap gap-1">
                          {genres.split(", ").map((g) => (
                            <span key={g} className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">{g}</span>
                          ))}
                        </div>
                      }
                    />
                  )}
                  {originalLanguage && (
                    <InfoRow icon={Globe} label="Langue originale" value={originalLanguage} />
                  )}
                  {director && (
                    <InfoRow icon={Clapperboard} label="Réalisateur" value={<span className="font-medium">{director}</span>} />
                  )}
                  {castList && (
                    <InfoRow icon={Users} label="Casting" value={castList} />
                  )}
                  {voteAverage && voteAverage > 0 && (
                    <InfoRow
                      icon={Star}
                      label="Note"
                      value={
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-black tracking-widest text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded uppercase">IMDb</span>
                          <span className="font-bold text-yellow-400 text-lg">{voteAverage.toFixed(1)}</span>
                          <span className="text-muted-foreground text-xs">/ 10</span>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(voteAverage / 2) ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/20"}`} />
                            ))}
                          </div>
                          <div className="flex-1 bg-muted/30 rounded-full h-1.5 overflow-hidden min-w-[60px]">
                            <div
                              className={`h-full rounded-full ${voteAverage >= 7 ? "bg-yellow-400" : voteAverage >= 5 ? "bg-orange-400" : "bg-red-500"}`}
                              style={{ width: `${(voteAverage / 10) * 100}%` }}
                            />
                          </div>
                        </div>
                      }
                    />
                  )}
                  <InfoRow icon={Zap} label="Disponibilité" value={isReleased ? "✅ Disponible en streaming" : "⏳ Bientôt disponible"} />
                  <InfoRow icon={Shield} label="Qualité" value="Full HD & 4K – VF / VOSTFR" />
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── TABLE 2 : COMPATIBILITÉ APPAREILS ── */}
        <section className="py-6 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Regarder {movie.title} sur tous vos appareils</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-primary/5">
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Appareil</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Compatibilité</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Qualité max</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { device: "Smart TV (Samsung, LG, Sony)", compat: "✅ Compatible", quality: "4K Ultra HD" },
                    { device: "Android TV / Fire Stick", compat: "✅ Compatible", quality: "Full HD" },
                    { device: "iPhone / iPad", compat: "✅ Compatible", quality: "Full HD" },
                    { device: "Android smartphone / tablette", compat: "✅ Compatible", quality: "Full HD" },
                    { device: "PC Windows / Mac", compat: "✅ Compatible", quality: "Full HD & 4K" },
                    { device: "Console (PS4, Xbox)", compat: "✅ Via navigateur", quality: "Full HD" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-border/40 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">{row.device}</td>
                      <td className="px-4 py-3 text-foreground">{row.compat}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.quality}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {genres && (
              <p className="mt-3 text-xs text-muted-foreground">
                {movie.title} ({genres}) — disponible en VF et VOSTFR, sans publicité ni coupure.
              </p>
            )}
          </div>
        </section>

        {/* ── Avantages ──────────────────────────────────────────────────── */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3 text-foreground">
              Pourquoi regarder <span className="text-primary">{movie.title}</span> sur RoiDuStreaming ?
            </h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
              {overview
                ? `Découvrez ${movie.title}${genres ? `, ${genres.toLowerCase()},` : ""} en VF et VOSTFR en qualité Full HD et 4K.`
                : isReleased
                  ? `${movie.title} est disponible en VF et VOSTFR en qualité Full HD et 4K, sur tous vos appareils.`
                  : `Dès sa sortie, ${movie.title} sera ajouté à notre catalogue. Découvrez déjà nos +100 000 films !`}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {advantages.map((adv, i) => (
                <div key={i} className="p-5 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-center group">
                  <adv.icon className="w-8 h-8 mx-auto mb-3 text-primary group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-foreground mb-1">{adv.title}</h3>
                  <p className="text-sm text-muted-foreground">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contenu SEO ────────────────────────────────────────────────── */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-3">
              {movie.title} en streaming VF sur RoiDuStreaming
            </h2>
            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base mb-4">
              Regardez <strong className="text-foreground">{movie.title}</strong>{releaseYear && ` (${releaseYear})`}{genres ? ` — ${genres}` : ""} en streaming VF et 4K sur RoiDuStreaming.
              {director && ` Réalisé par ${director}.`}
              {" "}+100 000 films disponibles sur{" "}
              <Link to="/tutoriels/iptv-smart-tv-android" className="text-primary hover:underline">Smart TV</Link>,{" "}
              <Link to="/tutoriels/iptv-android" className="text-primary hover:underline">Android</Link>,{" "}
              <Link to="/tutoriels/iptv-iphone-ipad" className="text-primary hover:underline">iPhone/iPad</Link>,{" "}
              <Link to="/tutoriels/iptv-windows" className="text-primary hover:underline">PC</Link> et{" "}
              <Link to="/tutoriels/iptv-firestick" className="text-primary hover:underline">Fire Stick</Link>.
            </p>
            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
              <Link to="/nos-offres" className="text-primary hover:underline font-medium">Abonnement dès 5€/mois</Link> — accès illimité à +25 000 séries et +15 000 chaînes TV.
              {voteAverage && voteAverage > 0 && ` Note : ${voteAverage.toFixed(1)}/10.`}
              {runtimeLabel && ` Durée : ${runtimeLabel}.`}
              {" "}Lisez nos <Link to="/avis-clients" className="text-primary hover:underline">avis clients</Link>.
            </p>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────────────── */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border border-primary/30 p-6 sm:p-8 md:p-12 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-foreground">
              Regardez {movie.title} maintenant !
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto text-sm sm:text-base">
              +100 000 films, +25 000 séries, +15 000 chaînes TV. À partir de 5€/mois. Essai gratuit 24h.
            </p>
            <div className="flex justify-center w-full">
              <TestRequestFormWithCountries
                trigger={
                  <Button className="btn-primary text-primary-foreground font-bold py-4 px-6 sm:py-6 sm:px-10 text-sm sm:text-base rounded-full group w-full max-w-sm sm:w-auto">
                    🎬 Démarrer mon essai gratuit 24h
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                }
              />
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────── */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-foreground">
              Questions fréquentes sur {movie.title}
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: `Où regarder ${movie.title} en streaming VF ?`, a: `Regardez ${movie.title} en streaming VF HD et 4K sur RoiDuStreaming. Abonnez-vous à partir de 5€/mois ou testez gratuitement pendant 24h.` },
                { q: `${movie.title} est-il disponible en version française (VF) ?`, a: `Oui, ${movie.title} est disponible en VF et VOSTFR en qualité Full HD et 4K sur RoiDuStreaming.` },
                { q: `${movie.title} est-il en 4K ?`, a: `Oui, ${movie.title} est disponible en Full HD et 4K sur RoiDuStreaming, sans publicité ni coupure.` },
                { q: `Sur quels appareils puis-je regarder ${movie.title} ?`, a: `${movie.title} est accessible sur Smart TV, Android, iPhone/iPad, PC, Mac et Amazon Fire Stick. Installation en 5 minutes.` },
                ...(runtimeLabel
                  ? [{ q: `Quelle est la durée de ${movie.title} ?`, a: `${movie.title} dure ${runtimeLabel} (${runtime} minutes).` }]
                  : []),
                { q: `Puis-je tester avant de m'abonner ?`, a: `Absolument ! Profitez d'un essai gratuit de 24h avec accès complet à tous les films et séries du catalogue.` },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border/50 rounded-xl px-5 bg-card/50">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ── VOIR AUSSI ────────────────────────────────────────────────── */}
        {relatedMovies && relatedMovies.length > 0 && (
          <section className="py-10 px-4 bg-card/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground mb-5">Voir aussi – Films similaires</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {relatedMovies.map((m) => (
                  <Link
                    key={m.slug}
                    to={`/films/${m.slug}`}
                    className="flex gap-3 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all group"
                  >
                    {(m as any).poster_url && (
                      <img
                        src={(m as any).poster_url}
                        alt={`${m.title} streaming VF`}
                        className="w-12 h-16 object-cover rounded-lg shrink-0"
                      />
                    )}
                    <div className="flex flex-col gap-1 min-w-0">
                      <span className="font-bold text-foreground group-hover:text-primary transition-colors text-sm line-clamp-2">{m.title}</span>
                      {m.release_date && <span className="text-xs text-muted-foreground">{new Date(m.release_date).getFullYear()}</span>}
                      {(m as any).vote_average > 0 && (
                        <span className="text-xs text-yellow-400 flex items-center gap-1">
                          <Star className="w-3 h-3 fill-yellow-400" /> {((m as any).vote_average as number).toFixed(1)}/10
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground flex items-center gap-1 mt-auto">
                        Voir <ChevronRight className="w-3 h-3 ml-auto" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Editorial Enrichment ── */}
        <EditorialEnrichment content={(movie as any).editorial_content} />

        {/* ── Offres & liens internes ───────────────────────────────────── */}
        <div className="max-w-6xl mx-auto px-4">
          <OffersBlock />
        </div>
        <PopularPagesBlock />
        <RecentPagesBlock />
        <SeoSiloLinks category="films" currentSlug={slug} />

        <Footer />
        <FloatingTestCTA />
      </article>
    </>
  );
};

export default MoviePage;
